import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Popover,
} from "@mui/material";
import NavBar from "../navbar";
import { validateLoggedIn, getSession } from "../utils";
import { Link, useNavigate } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import config from "../config";
import * as Sentry from "@sentry/react";
import { formatUtc } from "../utc";

const Users = () => {
    const [orderBy, setOrderBy] = useState("created_at");
    const [order, setOrder] = useState("desc");
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [filterEmailVerified, setFilterEmailVerified] = useState({
        yes: true,
        no: true,
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) {
            setError(validate);
            return;
        }

        const getUsers = async () => {
            try {
                const response = await fetch(config.apiBaseUrl + "/admin/users", {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "user-authorization": getSession().token,
                    },
                });
                if (!response.ok) {
                    throw new Error(`Error: ${response.status} ${response.statusText}`);
                }
                const jsonData = await response.json();
                setData(jsonData);
            } catch (err) {
                Sentry.captureException(err);
                setError(err.message);
            }
        };

        getUsers();
    }, []);

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(column);
    };

    const handleEmailVerifiedFilterChange = (event) => {
        setFilterEmailVerified((prev) => ({
            ...prev,
            [event.target.name]: event.target.checked,
        }));
    };

    const handlePopoverOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const popoverOpen = Boolean(anchorEl);

    const sortData = (rows, orderBy, order) => {
        return [...rows].sort((a, b) => {
            const valueA = a[orderBy];
            const valueB = b[orderBy];

            if (typeof valueA === "string" && typeof valueB === "string") {
                return order === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            }

            if (typeof valueA === "number" && typeof valueB === "number") {
                return order === "asc" ? valueA - valueB : valueB - valueA;
            }

            const dateA = new Date(valueA);
            const dateB = new Date(valueB);
            return order === "asc" ? dateA - dateB : dateB - dateA;
        });
    };

    if (error) {
        return (
            <div style={{ textAlign: "center", padding: "20px", color: "red" }}>
                <p>{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <CircularProgress />
            </div>
        );
    }

    if (data.length === 0) {
        return <div style={{ textAlign: "center", padding: "20px" }}>No users found</div>;
    }

    const filteredRows = data.filter((row) => {
        const isVerified = !!row.email_verified ;
        return (isVerified && filterEmailVerified.yes) || (!isVerified && filterEmailVerified.no);
    });

    const sortedRows = sortData(filteredRows, orderBy, order);

    return (
        <>
            <NavBar />
            <Button
                variant="contained"
                color="primary"
                style={{ margin: "20px" }}
                onClick={() => navigate("/users/new")}
            >
                New
            </Button>

            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="users table">
                    <TableHead>
                        <TableRow>
                            <SortableTableCell column="id" label="ID" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="login" label="Login" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="role" label="Role" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="email" label="Email" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="name" label="Name" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="created_at" label="Created At" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="modified_at" label="Modified At" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <SortableTableCell column="last_login_at" label="Last Login" orderBy={orderBy} order={order} handleSort={handleSort} />
                            <TableCell
                                onMouseEnter={handlePopoverOpen}
                                onMouseLeave={handlePopoverClose}
                                style={{
                                    cursor: "pointer",
                                    color: "#1976d2",
                                    textDecoration: "underline",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Is Email Verified
                                <Popover
                                    open={popoverOpen}
                                    anchorEl={anchorEl}
                                    onClose={handlePopoverClose}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }}
                                    disableRestoreFocus
                                >
                                    <FormGroup style={{ padding: "10px 20px" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={filterEmailVerified.yes}
                                                    onChange={handleEmailVerifiedFilterChange}
                                                    name="yes"
                                                />
                                            }
                                            label="Yes"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={filterEmailVerified.no}
                                                    onChange={handleEmailVerifiedFilterChange}
                                                    name="no"
                                                />
                                            }
                                            label="No"
                                        />
                                    </FormGroup>
                                </Popover>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <Link to={`/users/${row.id}/edit`}>{row.id}</Link>
                                </TableCell>
                                <TableCell>{row.login}</TableCell>
                                <TableCell>{row.role}</TableCell>
                                <TableCell>{row.email}</TableCell>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{formatUtc(row.created_at)}</TableCell>
                                <TableCell>{formatUtc(row.modified_at)}</TableCell>
                                <TableCell>{formatUtc(row.last_login_at)}</TableCell>
                                <TableCell>{row.email_verified ? "Yes" : "No"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const SortableTableCell = ({ column, label, orderBy, order, handleSort }) => (
    <TableCell onClick={() => handleSort(column)} style={{ cursor: "pointer" }}>
        {label}{" "}
        {orderBy === column &&
            (order === "asc" ? (
                <ArrowUpwardIcon fontSize="small" />
            ) : (
                <ArrowDownwardIcon fontSize="small" />
            ))}
    </TableCell>
);

export default Users;
